import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner, InputGroup} from 'react-bootstrap'
import {
  getAgreementById,
  submitChapterToReview,
  getChapterById,
  getParentChapters,
  verifySourceUrl,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useNavigate, useParams} from 'react-router-dom'
import {AgreementChapterNew} from './core/_models'
import './TradeDataAgreementChapterNewPage.scss'

import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import NoPermissionCard from '../../../../_metronic/layout/components/permission/NoPermissionCard'
import SummernoteEditor from '../../../../ui-kit/SummerNote/SummerNote'
import RTAContentParentDropdown from '../../../../ui-kit/RTAContentParentDropdown'

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

const TradeDataAgreementChapterNewPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {idAgreement} = useParams()
  const {idChapter} = useParams()
  const isEditMode = Boolean(idChapter)

  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)

  const [isCodeMode, setIsCodeMode] = useState(false)
  const [codeValue, setCodeValue] = useState('')
  const [wordCount, setWordCount] = useState(0)
  const [parentChapters, setParentChapters] = useState<AgreementChapterNew[]>([])
  const [agreementChapter, setAgreementChapter] = useState<AgreementChapterNew>({
    id: '',
    parent_id: '',
    type: '',
    label: '',
    title: '',
    rta_id: '',
    clean_content: '',
    nwords_new: 0,
    display_content: '',
    sort_order: 0,
    children: [],
    source: '',
    is_source_valid: null,
  })
  const [isSourceLinkVerified, setIsSourceLinkVerified] = useState<boolean | null>(null)
  const [isLoadingVerify, setIsLoadingVerify] = useState(false)

  const [originalChapter, setOriginalChapter] = useState<AgreementChapterNew | null>(null)

  const handleSourceLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSourceLinkVerified(null)
    const {value} = e.target
    setAgreementChapter((prev) => ({
      ...prev,
      source: value,
      is_source_valid: null,
    }))
  }

  const handleVerifySourceUrl = async (url: string) => {
    setIsLoadingVerify(true)
    try {
      const response = await verifySourceUrl(axiosPrivate, url)
      const isValid = response.data.isVerified === true
      setIsSourceLinkVerified(isValid)
      setAgreementChapter((prev) => ({
        ...prev,
        is_source_valid: isValid,
      }))
    } catch (error) {
      setIsSourceLinkVerified(false)
      setAgreementChapter((prev) => ({...prev, is_source_valid: false}))
    } finally {
      setIsLoadingVerify(false)
    }
  }

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      let detailRes, parentChapters

      if (isEditMode && idChapter) {
        detailRes = await getChapterById(axiosPrivate, {idChapter})
      } else if (idAgreement) {
        detailRes = await getAgreementById(axiosPrivate, {idAgreement})
      }

      if (detailRes?.data?.data) {
        const resData = detailRes.data.data
        setAgreementChapter((prev) => ({
          ...prev,
          ...resData,
        }))
        setOriginalChapter(resData)

        parentChapters = await getParentChapters(axiosPrivate, {
          idAgreement: resData.rta_id ?? idAgreement,
        })
        setParentChapters(parentChapters?.data?.data || [])

        const articleTextDisplay = resData.article_text_display ?? ''
        setWordCount(countWordsFromHtml(articleTextDisplay))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAgreement(false)
    }
  }

  const FIELDS_TO_COMPARE = [
    'label',
    'title',
    'parent_id',
    'type',
    'display_content',
    'display_content',
    'clean_content',
    'sort_order',
    'source',
  ]

  function isChapterChanged(original: any, current: any) {
    for (const key of FIELDS_TO_COMPARE) {
      const oldVal = String(original?.[key] ?? '').trim()
      const newVal = String(current?.[key] ?? '').trim()
      if (oldVal !== newVal) return true // found difference
    }
    return false
  }

  useEffect(() => {
    fetchOptionAgreement()
  }, [])

  // Add or Edit
  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setAgreementChapter((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = ['title', 'label', 'type']

    const isValid = requiredFields.every((key) => {
      const val = (agreementChapter as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  const convertFromHTML = (html: string): string => {
    const text = html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    return text
  }

  const countWordsFromHtml = (html: string): number => {
    const text = convertFromHTML(html)
    if (!text) return 0
    return text.split(' ').length
  }

  useEffect(() => {
    validateRequiredFields()
  }, [agreementChapter])

  const handleSubmit = () => {
    const finalHtml = isCodeMode ? codeValue : agreementChapter.display_content || ''
    const finalWordCount = countWordsFromHtml(finalHtml)
    setWordCount(finalWordCount)

    if (ability.can('update', subject) && originalChapter) {
      const isChanged = isChapterChanged(originalChapter, agreementChapter)
      if (!isChanged) {
        ReactSwal.fire({
          title: 'No changes detected',
          text: 'You haven’t made any modifications to the data.',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Go back to list',
          cancelButtonText: 'Keep editing',
          customClass: {
            title: 'fw-bold',
            confirmButton: 'btn btn-danger btn-sm',
            cancelButton: 'btn btn-success btn-sm',
          },
          padding: '1rem',
        }).then((result) => {
          if (result.isConfirmed) {
            navigate(`/new-rta/${idAgreement ?? agreementChapter.rta_id}`)
          }
        })
        return
      }
    }

    const body = {
      type: agreementChapter.type,
      label: agreementChapter.label,
      title: agreementChapter.title,
      rta_id: idAgreement ?? agreementChapter.rta_id,
      clean_content: convertFromHTML(agreementChapter.display_content ?? ''),
      nwords_new: finalWordCount,
      display_content: agreementChapter.display_content,
      parent_id: agreementChapter.parent_id,
      sort_order: agreementChapter.sort_order,
      rta_content_id: isEditMode ? agreementChapter.id : null,
      source: agreementChapter.source || '',
      is_source_valid: agreementChapter.source?.trim() ? isSourceLinkVerified ?? false : null,
    }

    confirmDialog(
      `Are you sure you want to ${isEditMode ? 'update' : 'create'} this agreement?`
    ).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      submitChapterToReview(axiosPrivate, body)
        .then((res) => {
          if (res.status === 201) {
            ReactSwal.fire({
              title: ability.can('create', subject)
                ? 'Data submitted'
                : 'Data submitted for review',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              navigate(`/new-rta/${idAgreement ?? agreementChapter.rta_id}`)
            })
          }
        })
        .catch((err) => {
          console.error(err)
          ReactSwal.fire('Error', 'Failed to create data', 'error')
        })
        .finally(() => {
          setIsLoadingButton(false)
        })
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        navigate(`/new-rta/${idAgreement ?? agreementChapter.rta_id}`)
      }
    })
  }
  // End of Add or Edit

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'new-rta',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: agreementChapter.title ?? '-',
            path: `new-rta/${idAgreement ?? agreementChapter.rta_id}`,
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isEditMode ? 'Edit' : 'Add'}
      </PageTitle>
      <div className='TradeDataAgreementChapterPage'>
        {ability.can('update', subject) || ability.can('create', subject) ? (
          <Row className='mb-5'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>
                          {isEditMode ? `Edit Chapter` : 'Add New Chapter'}
                        </h3>
                      </div>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {!isLoadingAgreement ? (
                    <Form className='form-agreement'>
                      <div className='row'>
                        <div className='col-12'>
                          <h3>Agreement Information</h3>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Name
                          </Form.Label>
                          <div className='data-value'>{agreementChapter.agreement}</div>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Agreement ID
                          </Form.Label>
                          <div className='data-value'>{idAgreement}</div>
                        </div>
                      </div>
                      <hr />
                      <div className='row'>
                        <div className='col-12'>
                          <h3>Content Information</h3>
                        </div>
                        <div className='col-12'>
                          <Form.Label className='required text-primary me-0 ps-0' column>
                            Content Type
                          </Form.Label>
                          <Form.Select
                            name='type'
                            value={agreementChapter.type ?? ''}
                            onChange={handleValueChange}
                          >
                            <option value=''>Select Content Type</option>
                            <option value='chapter'>Chapter</option>
                            <option value='preamble'>Preamble</option>
                            <option value='part'>Part</option>
                            <option value='annex'>Annex</option>
                            <option value='content'>Content</option>
                            <option value='article'>Article</option>
                            <option value='section'>Section</option>
                            <option value='sub-section'>Sub-section</option>
                          </Form.Select>
                        </div>
                        <div className='col-12 col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Content Parent
                          </Form.Label>
                          <RTAContentParentDropdown
                            onSelect={(parent_id) =>
                              setAgreementChapter({...agreementChapter, parent_id: parent_id})
                            }
                            originalParent={agreementChapter.parent_id}
                            idAgreement={idAgreement ?? agreementChapter.rta_id}
                          />
                        </div>
                        <div className='col-12 col-md-6'>
                          <Form.Label className='required text-primary me-0 ps-0' column>
                            Content Label
                          </Form.Label>
                          <Form.Control
                            type='text'
                            name='label'
                            placeholder={'Content Label'}
                            value={agreementChapter.label ?? ''}
                            onChange={handleValueChange}
                          />
                        </div>
                        <div className='col-12 col-md-6'>
                          <Form.Label className='required text-primary me-0 ps-0' column>
                            Content Title
                          </Form.Label>
                          <Form.Control
                            type='text'
                            name='title'
                            placeholder={'Content Title'}
                            value={agreementChapter.title ?? ''}
                            onChange={handleValueChange}
                          />
                        </div>

                        <div className='col-12 col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Sort Order
                          </Form.Label>
                          <Form.Control
                            type='text'
                            value={agreementChapter.sort_order ?? ''}
                            readOnly={true}
                          />
                        </div>
                        <div className='row'>
                          <div className='col-md-12 mt-3'>
                            <div className='d-flex justify-content-between align-items-start mb-2'>
                              <div>
                                <Form.Label className='text-primary m-0'>
                                  Content Text Display
                                </Form.Label>
                              </div>
                              <div className='text-muted small'>{wordCount} words</div>
                            </div>
                            <SummernoteEditor
                              value={agreementChapter.display_content || ''}
                              onChange={(html) => {
                                setAgreementChapter((prev) => ({
                                  ...prev,
                                  display_content: html,
                                }))
                                setWordCount(countWordsFromHtml(html))
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <hr />
                      <div className='row'>
                        <div className='col-12'>
                          <h3>Source</h3>
                        </div>
                        <div className='col-md-12 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Link
                          </Form.Label>
                          {isSourceLinkVerified !== null && (
                            <div className='mb-2'>
                              <Form.Text
                                className={isSourceLinkVerified ? 'text-success' : 'text-danger'}
                              >
                                {isSourceLinkVerified ? 'Valid URL' : 'Invalid URL'}
                              </Form.Text>
                            </div>
                          )}
                          <InputGroup hasValidation>
                            <Form.Control
                              type='text'
                              name='source'
                              isInvalid={
                                !!agreementChapter.source &&
                                !/^https?:\/\//.test(agreementChapter.source)
                              }
                              placeholder='Enter link source'
                              value={agreementChapter.source ?? ''}
                              onChange={handleSourceLinkChange}
                            />
                            <Button
                              variant='outline-secondary'
                              disabled={
                                !agreementChapter.source?.trim() ||
                                !/^https?:\/\//.test(agreementChapter.source) ||
                                isLoadingVerify
                              }
                              onClick={() => handleVerifySourceUrl(agreementChapter.source!)}
                            >
                              {isLoadingVerify && (
                                <Spinner animation='border' size='sm' className='me-2' />
                              )}
                              Verify
                            </Button>
                            <Form.Control.Feedback type='invalid'>
                              URL must start with http:// or https://
                            </Form.Control.Feedback>
                          </InputGroup>
                        </div>
                      </div>
                      <hr />
                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            onClick={handleSubmit}
                            disabled={
                              isLoadingButton ||
                              !isFormValid ||
                              (agreementChapter.source!.trim().length > 0 &&
                                agreementChapter.is_source_valid === null)
                            }
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{height: '50vh'}}
                    >
                      <Spinner
                        animation='border'
                        role='status'
                        variant='primary'
                        className='me-3'
                      />
                      <div className='loading-text'>
                        Loading
                        <span className='dot'>.</span>
                        <span className='dot'>.</span>
                        <span className='dot'>.</span>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          <NoPermissionCard />
        )}
      </div>
    </>
  )
}

export default TradeDataAgreementChapterNewPage
